/**
 * Mock data for Dashboard V2 Finance tab.
 * Used by Performance dashboard and UI sections without live API hooks yet.
 */

import type { RangeKey } from '@dataTypes/dashboard.types'
import type { ProductionByProcedureRow } from '@services/api/dashboard/finance.api'
import { computeProductionByProcedureCardAxis } from '@utils/productionByProcedureCategory.util'

// ---------------------------------------------------------------------------
// AR Aging
// ---------------------------------------------------------------------------

export const AR_TOTAL_DATA = [
  { label: '0-30', value: 10000 },
  { label: '31-60', value: 8000 },
  { label: '61-90', value: 4000 },
  { label: '90+', value: 2000 },
]

export const AR_BUCKET_COLORS: Record<string, string> = {
  '0-30': 'var(--blue-600)',
  '31-60': 'var(--blue-500)',
  '61-90': 'var(--blue-300)',
  '90+': 'var(--orange-300)',
}

export const FINANCE_AR_AGING_BAR_SIZE = 100

export const AR_BUCKETS = ['0-30', '31-60', '61-90', '90+'] as const

type ARBucket = (typeof AR_BUCKETS)[number]

/** Mock values per bucket for progress bars (AR Insurance). Aligns with bar chart. */
export const AR_INSURANCE_PROGRESS_DATA: Record<ARBucket, number> = {
  '0-30': 10000,
  '31-60': 8000,
  '61-90': 4000,
  '90+': 2000,
}

/** Mock values per bucket for progress bars (AR Patient). */
export const AR_PATIENT_PROGRESS_DATA: Record<ARBucket, number> = {
  '0-30': 4200,
  '31-60': 2400,
  '61-90': 1800,
  '90+': 1000,
}

export const AR_INSURANCE_PROGRESS_MAX = Math.max(
  ...Object.values(AR_INSURANCE_PROGRESS_DATA),
)
export const AR_PATIENT_PROGRESS_MAX = Math.max(
  ...Object.values(AR_PATIENT_PROGRESS_DATA),
)

// ---------------------------------------------------------------------------
// Treatment value
// ---------------------------------------------------------------------------

export const TREATMENT_VALUE_DATA = [
  { label: 'Billed', value: 24.1 },
  { label: 'Collected', value: 9.4 },
  { label: 'Potential', value: 16.4 },
]

export const TREATMENT_VALUE_COLORS: Record<string, string> = {
  Billed: 'var(--blue-600)',
  Collected: 'var(--blue-400)',
  Potential: 'var(--orange-300)',
}

export const FINANCE_TREATMENT_VALUE_BAR_SIZE = 150

/** DSO grouped bar chart (Performance): 90px bars, 5px intra-group gap per Figma. */
export const FINANCE_DSO_BAR_SIZE = 90

// ---------------------------------------------------------------------------
// Outstanding balance table
// ---------------------------------------------------------------------------

export const OUTSTANDING_BALANCE_COLUMNS = [
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    linkToPatientProfile: true,
  },
  { key: 'charges', label: 'Charges', sortable: true },
  { key: 'payments', label: 'Payments', sortable: true },
  { key: 'balance', label: 'Balance', sortable: true },
]

export type OutstandingBalanceRow = {
  id: string
  name: string
  charges: string
  payments: string
  balance: string
}

const OUTSTANDING_BALANCE_BY_RANGE: Record<RangeKey, OutstandingBalanceRow[]> =
  {
    '1D': [
      {
        id: '1',
        name: 'John Doe',
        charges: '$1,250',
        payments: '$800',
        balance: '$450',
      },
      {
        id: '2',
        name: 'Frances Ramirez',
        charges: '$1,200',
        payments: '$150',
        balance: '$720',
      },
      {
        id: '3',
        name: 'Devin Smith',
        charges: '$1,050',
        payments: '$200',
        balance: '$850',
      },
      {
        id: '4',
        name: 'Ava Johnson',
        charges: '$920',
        payments: 'N/A',
        balance: '$920',
      },
      {
        id: '5',
        name: 'Liam Brown',
        charges: '$980',
        payments: '$250',
        balance: '$730',
      },
      {
        id: '6',
        name: 'Sophia Garcia',
        charges: '$870',
        payments: '$300',
        balance: '$570',
      },
      {
        id: '7',
        name: 'Mason Davis',
        charges: '$800',
        payments: '$350',
        balance: '$450',
      },
      {
        id: '8',
        name: 'Isabella Martinez',
        charges: '$750',
        payments: '$400',
        balance: '$350',
      },
    ],
    '1W': [
      {
        id: 'w1',
        name: 'John Doe',
        charges: '$6,400',
        payments: '$4,100',
        balance: '$2,300',
      },
      {
        id: 'w2',
        name: 'Frances Ramirez',
        charges: '$5,980',
        payments: '$1,240',
        balance: '$3,740',
      },
      {
        id: 'w3',
        name: 'Devin Smith',
        charges: '$5,420',
        payments: '$1,880',
        balance: '$3,540',
      },
      {
        id: 'w4',
        name: 'Ava Johnson',
        charges: '$4,960',
        payments: 'N/A',
        balance: '$4,960',
      },
      {
        id: 'w5',
        name: 'Liam Brown',
        charges: '$5,100',
        payments: '$1,620',
        balance: '$3,480',
      },
      {
        id: 'w6',
        name: 'Sophia Garcia',
        charges: '$4,780',
        payments: '$2,040',
        balance: '$2,740',
      },
      {
        id: 'w7',
        name: 'Mason Davis',
        charges: '$4,520',
        payments: '$2,180',
        balance: '$2,340',
      },
      {
        id: 'w8',
        name: 'Isabella Martinez',
        charges: '$4,200',
        payments: '$2,400',
        balance: '$1,800',
      },
      {
        id: 'w9',
        name: 'Noah Wilson',
        charges: '$3,880',
        payments: '$1,100',
        balance: '$2,780',
      },
      {
        id: 'w10',
        name: 'Emma Thompson',
        charges: '$3,640',
        payments: '$2,900',
        balance: '$740',
      },
    ],
    '1M': [
      {
        id: 'm1',
        name: 'John Doe',
        charges: '$24,800',
        payments: '$18,200',
        balance: '$6,600',
      },
      {
        id: 'm2',
        name: 'Frances Ramirez',
        charges: '$22,400',
        payments: '$9,800',
        balance: '$12,600',
      },
      {
        id: 'm3',
        name: 'Devin Smith',
        charges: '$21,200',
        payments: '$11,400',
        balance: '$9,800',
      },
      {
        id: 'm4',
        name: 'Ava Johnson',
        charges: '$19,600',
        payments: 'N/A',
        balance: '$19,600',
      },
      {
        id: 'm5',
        name: 'Liam Brown',
        charges: '$18,900',
        payments: '$8,700',
        balance: '$10,200',
      },
      {
        id: 'm6',
        name: 'Sophia Garcia',
        charges: '$17,400',
        payments: '$10,100',
        balance: '$7,300',
      },
      {
        id: 'm7',
        name: 'Mason Davis',
        charges: '$16,200',
        payments: '$9,400',
        balance: '$6,800',
      },
      {
        id: 'm8',
        name: 'Isabella Martinez',
        charges: '$15,800',
        payments: '$11,200',
        balance: '$4,600',
      },
      {
        id: 'm9',
        name: 'Noah Wilson',
        charges: '$14,200',
        payments: '$6,800',
        balance: '$7,400',
      },
      {
        id: 'm10',
        name: 'Emma Thompson',
        charges: '$13,600',
        payments: '$12,400',
        balance: '$1,200',
      },
      {
        id: 'm11',
        name: 'Olivia Chen',
        charges: '$12,800',
        payments: '$4,200',
        balance: '$8,600',
      },
      {
        id: 'm12',
        name: 'Ethan Brooks',
        charges: '$11,400',
        payments: '$5,600',
        balance: '$5,800',
      },
    ],
    '1Y': [
      {
        id: 'y1',
        name: 'John Doe',
        charges: '$286K',
        payments: '$214K',
        balance: '$72K',
      },
      {
        id: 'y2',
        name: 'Frances Ramirez',
        charges: '$262K',
        payments: '$118K',
        balance: '$144K',
      },
      {
        id: 'y3',
        name: 'Devin Smith',
        charges: '$248K',
        payments: '$132K',
        balance: '$116K',
      },
      {
        id: 'y4',
        name: 'Ava Johnson',
        charges: '$228K',
        payments: 'N/A',
        balance: '$228K',
      },
      {
        id: 'y5',
        name: 'Liam Brown',
        charges: '$218K',
        payments: '$102K',
        balance: '$116K',
      },
      {
        id: 'y6',
        name: 'Sophia Garcia',
        charges: '$204K',
        payments: '$118K',
        balance: '$86K',
      },
      {
        id: 'y7',
        name: 'Regional plan / unassigned',
        charges: '$196K',
        payments: '$142K',
        balance: '$54K',
      },
      {
        id: 'y8',
        name: 'Isabella Martinez',
        charges: '$188K',
        payments: '$132K',
        balance: '$56K',
      },
    ],
    YTD: [
      {
        id: 'yt1',
        name: 'John Doe',
        charges: '$72K',
        payments: '$54K',
        balance: '$18K',
      },
      {
        id: 'yt2',
        name: 'Frances Ramirez',
        charges: '$66K',
        payments: '$30K',
        balance: '$36K',
      },
      {
        id: 'yt3',
        name: 'Devin Smith',
        charges: '$62K',
        payments: '$34K',
        balance: '$28K',
      },
      {
        id: 'yt4',
        name: 'Ava Johnson',
        charges: '$58K',
        payments: 'N/A',
        balance: '$58K',
      },
      {
        id: 'yt5',
        name: 'Liam Brown',
        charges: '$54K',
        payments: '$26K',
        balance: '$28K',
      },
      {
        id: 'yt6',
        name: 'Sophia Garcia',
        charges: '$50K',
        payments: '$30K',
        balance: '$20K',
      },
      {
        id: 'yt7',
        name: 'Mason Davis',
        charges: '$48K',
        payments: '$28K',
        balance: '$20K',
      },
      {
        id: 'yt8',
        name: 'Isabella Martinez',
        charges: '$46K',
        payments: '$32K',
        balance: '$14K',
      },
      {
        id: 'yt9',
        name: 'Noah Wilson',
        charges: '$42K',
        payments: '$20K',
        balance: '$22K',
      },
    ],
  }

const OUTSTANDING_BALANCE_SUMMARY: Record<
  RangeKey,
  {
    totalBalance: string
    totalCharges: string
    totalPayments: string
    accountsWithBalance: string
  }
> = {
  '1D': {
    totalBalance: '$5.0K',
    totalCharges: '$7.7K',
    totalPayments: '$2.7K',
    accountsWithBalance: '8',
  },
  '1W': {
    totalBalance: '$28.4K',
    totalCharges: '$46.9K',
    totalPayments: '$18.5K',
    accountsWithBalance: '10',
  },
  '1M': {
    totalBalance: '$94.2K',
    totalCharges: '$198K',
    totalPayments: '$104K',
    accountsWithBalance: '12',
  },
  '1Y': {
    totalBalance: '$890K',
    totalCharges: '$1.83M',
    totalPayments: '$988K',
    accountsWithBalance: '8',
  },
  YTD: {
    totalBalance: '$244K',
    totalCharges: '$498K',
    totalPayments: '$254K',
    accountsWithBalance: '9',
  },
}

export function getOutstandingBalanceTableData(
  range: RangeKey = '1D',
): OutstandingBalanceRow[] {
  return OUTSTANDING_BALANCE_BY_RANGE[range]
}

export function getOutstandingBalanceSummary(
  range: RangeKey = '1D',
): (typeof OUTSTANDING_BALANCE_SUMMARY)[RangeKey] {
  return OUTSTANDING_BALANCE_SUMMARY[range]
}

// ---------------------------------------------------------------------------
// Claims chart
// ---------------------------------------------------------------------------

export const CLAIMS_CHART_DATA = [
  { label: 'Submitted', value: 42.1 },
  { label: 'Paid', value: 26.4 },
  { label: 'Pending', value: 14.6 },
  { label: 'Denied', value: 4.4 },
]

export const CLAIMS_CHART_COLORS: Record<string, string> = {
  'Under review': 'var(--blue-500)',
  Paid: 'var(--vivid-green-400)',
  Pending: 'var(--yellow-400)',
  Denied: 'var(--red-400)',
}

// ---------------------------------------------------------------------------
// Production by procedure
// ---------------------------------------------------------------------------

/** Production by procedure bar heights ($K) — dev mock (13 categories). */
export const PRODUCTION_BY_PROCEDURE_DATA = [
  { label: 'Diagnostics', value: 10.2 },
  { label: 'Restorative', value: 8.8 },
  { label: 'Preventive', value: 7.5 },
  { label: 'Endo', value: 5.2 },
  { label: 'Periodontics', value: 4.6 },
  { label: 'Prosthodontics', value: 4.1 },
  { label: 'Implants', value: 3.4 },
  { label: 'Surgery', value: 2.8 },
  { label: 'Orthodontics', value: 2.5 },
  { label: 'Cosmetics', value: 2.2 },
  { label: 'Pediatric', value: 1.8 },
  { label: 'Emergency', value: 1.4 },
  { label: 'Other', value: 1.0 },
]

/** Per-category bar colors — 13 procedure types. */
export const PRODUCTION_BY_PROCEDURE_COLORS: Record<string, string> = {
  Diagnostics: 'var(--blue-700)',
  Restorative: 'var(--blue-600)',
  Preventive: 'var(--blue-500)',
  Endo: 'var(--blue-400)',
  Periodontics: 'var(--indigo-500)',
  Prosthodontics: 'var(--indigo-400)',
  Implants: 'var(--indigo-300)',
  Surgery: 'var(--blue-300)',
  Orthodontics: 'var(--orange-500)',
  Cosmetics: 'var(--orange-400)',
  Pediatric: 'var(--orange-300)',
  Emergency: 'var(--red-400)',
  Other: 'var(--vivid-green-400)',
}

export type FinanceProductionByProcedurePanel = {
  changeSignedPct: string
  changeAmt: string
  goalLabel: string
  gross: string
  net: string
  goalValue: string
  adjProductionRatio: string
}

const PRODUCTION_BY_PROCEDURE_PANEL_BY_RANGE: Record<
  RangeKey,
  FinanceProductionByProcedurePanel
> = {
  '1D': {
    changeSignedPct: '+8%',
    changeAmt: '$630',
    goalLabel: 'Goal 9K',
    gross: '$8.4K',
    net: '$7.2K',
    goalValue: '$9K',
    adjProductionRatio: '14.3%',
  },
  '1W': {
    changeSignedPct: '+5%',
    changeAmt: '$2.8K',
    goalLabel: 'Goal 58K',
    gross: '$48.2K',
    net: '$41.6K',
    goalValue: '$58K',
    adjProductionRatio: '13.8%',
  },
  '1M': {
    changeSignedPct: '+6%',
    changeAmt: '$14K',
    goalLabel: 'Goal 240K',
    gross: '$198K',
    net: '$172K',
    goalValue: '$240K',
    adjProductionRatio: '14.1%',
  },
  '1Y': {
    changeSignedPct: '+4%',
    changeAmt: '$128K',
    goalLabel: 'Goal 2.4M',
    gross: '$2.08M',
    net: '$1.82M',
    goalValue: '$2.4M',
    adjProductionRatio: '14.0%',
  },
  YTD: {
    changeSignedPct: '+5%',
    changeAmt: '$41K',
    goalLabel: 'Goal 820K',
    gross: '$712K',
    net: '$618K',
    goalValue: '$820K',
    adjProductionRatio: '14.0%',
  },
}

export function getFinanceProductionByProcedurePanel(
  range: RangeKey = '1D',
): FinanceProductionByProcedurePanel {
  return PRODUCTION_BY_PROCEDURE_PANEL_BY_RANGE[range]
}

// ---------------------------------------------------------------------------
// Production per provider table
// ---------------------------------------------------------------------------

export const PRODUCTION_PER_PROVIDER_COLUMNS = [
  { key: 'provider', label: 'Provider', sortable: true },
  { key: 'credential', label: 'Credential', sortable: true },
  {
    key: 'totalProcedures',
    label: 'Total procedures',
    sortable: true,
    formatter: (
      _: unknown,
      row: { totalProcedures?: string; proceduresRate?: string },
    ) => `${row.totalProcedures ?? ''} ${row.proceduresRate ?? ''}`.trim(),
  },
  { key: 'production', label: 'Production', sortable: true },
]

export const PRODUCTION_PER_PROVIDER_DATA = [
  {
    id: '1',
    provider: 'Dr. Jack Monroe',
    credential: 'HYG',
    totalProcedures: '6',
    proceduresRate: '(0.8/hour)',
    production: '$2,248.00',
  },
  {
    id: '2',
    provider: 'Dr. Paula Tran',
    credential: 'DMD',
    totalProcedures: '8',
    proceduresRate: '(1.0/hour)',
    production: '$3,560.00',
  },
  {
    id: '3',
    provider: 'Dr. James Hwang',
    credential: 'DDS',
    totalProcedures: '4',
    proceduresRate: '(0.5/hour)',
    production: '$1,189.00',
  },
  {
    id: '4',
    provider: 'Dr. Maria Lopez',
    credential: 'DMD',
    totalProcedures: '1',
    proceduresRate: '(1.1/hour)',
    production: '$520.00',
  },
  {
    id: '5',
    provider: 'Dr. Ahmed Khan',
    credential: 'BDS',
    totalProcedures: '10',
    proceduresRate: '(1.2/hour)',
    production: '$1,075.00',
  },
]

export const PRODUCTION_PER_PROVIDER_TOTAL = '$8,492.00'

// ---------------------------------------------------------------------------
// Production & collection charts
// ---------------------------------------------------------------------------

/** Production chart: values in $K, scale 0–12 with Goal 10K. */
export const PRODUCTION_CHART_DATA = [
  { label: 'Jan 2026', Gross: 8.2, Net: 7.4 },
  { label: 'Feb 2026', Gross: 8.8, Net: 8.0 },
  { label: 'Mar 2026', Gross: 9.4, Net: 8.6 },
  { label: 'Apr 2026', Gross: 10.2, Net: 9.2 },
]

/** Collection performance chart (values in $K, scale 0–400 with Goal $350K). */
export const PRODUCTION_COLLECTION_CHART_DATA = [
  { label: 'Jan 2026', Gross: 220, Net: 200 },
  { label: 'Feb 2026', Gross: 250, Net: 230 },
  { label: 'Mar 2026', Gross: 284.1, Net: 254.6 },
  { label: 'Apr 2026', Gross: 300, Net: 270 },
]

/** Production & Collection performance grouped bars (Figma Gross/Net palette). */
export const FINANCE_GROSS_NET_BAR_COLORS = {
  gross: 'var(--blue-400)',
  net: 'var(--orange-300)',
} as const

export const PRODUCTION_COLLECTION_SERIES = [
  {
    dataKey: 'Gross',
    name: 'Gross',
    color: FINANCE_GROSS_NET_BAR_COLORS.gross,
  },
  { dataKey: 'Net', name: 'Net', color: FINANCE_GROSS_NET_BAR_COLORS.net },
]

export const COLLECTION_PERFORMANCE_SERIES = [
  {
    dataKey: 'Gross',
    name: 'Gross',
    color: FINANCE_GROSS_NET_BAR_COLORS.gross,
  },
  { dataKey: 'Net', name: 'Net', color: FINANCE_GROSS_NET_BAR_COLORS.net },
]

/** Collection line chart (Gross, Net, Goal) – same structure as Production. */
export const COLLECTION_CHART_DATA = [
  { label: 'Jan 7', Gross: 8.4, Net: 7.2 },
  { label: 'Jan 8', Gross: 9, Net: 8 },
]

// ---------------------------------------------------------------------------
// Range-keyed data (1D / 1W / 1M / 1Y / YTD) — same header tabs as Overview / Operations
// ---------------------------------------------------------------------------

const FINANCE_VS: Record<RangeKey, string> = {
  '1D': 'vs. yesterday',
  '1W': 'vs. last week',
  '1M': 'vs. last month',
  '1Y': 'vs. last year',
  YTD: 'vs. last YTD',
}

export function financeVsSuffix(range: RangeKey): string {
  return FINANCE_VS[range]
}

export function financeTrendLine(
  range: RangeKey,
  signedPct: string,
  amt: string,
): string {
  return `${signedPct} (${amt}) ${FINANCE_VS[range]}`
}

export const FINANCE_PERIOD_RIGHT_LABEL: Record<RangeKey, string> = {
  '1D': 'Today',
  '1W': 'This week',
  '1M': 'This month',
  '1Y': 'This year',
  YTD: 'YTD',
}

export type FinancePracticeRevenueCard = {
  leftLabel: string
  rightLabel?: string
  value: string
  footerUp: boolean
  footerPct: string
  footerAmt: string
}

const PRACTICE_REVENUE_BY_RANGE: Record<
  RangeKey,
  FinancePracticeRevenueCard[]
> = {
  '1D': [
    {
      leftLabel: 'Gross collection',
      rightLabel: 'Goal $19K',
      value: '$85.5K',
      footerUp: true,
      footerPct: '6%',
      footerAmt: '$800',
    },
    {
      leftLabel: 'Net collection',
      rightLabel: 'Goal $2.3K',
      value: '$82.5K',
      footerUp: false,
      footerPct: '6%',
      footerAmt: '$800',
    },
    {
      leftLabel: 'Adjustments',
      value: '3.0k',
      footerUp: true,
      footerPct: '1%',
      footerAmt: '$850',
    },
    {
      leftLabel: 'Net profit',
      value: '$58.4K',
      footerUp: false,
      footerPct: '6%',
      footerAmt: '$800',
    },
  ],
  '1W': [
    {
      leftLabel: 'Gross collection',
      rightLabel: 'Goal $95K',
      value: '$412K (61%)',
      footerUp: true,
      footerPct: '4%',
      footerAmt: '$18K',
    },
    {
      leftLabel: 'Net collection',
      rightLabel: 'Goal $88K',
      value: '$398K',
      footerUp: true,
      footerPct: '5%',
      footerAmt: '$21K',
    },
    {
      leftLabel: 'Adjustments',
      value: '$14.2K',
      footerUp: true,
      footerPct: '2%',
      footerAmt: '$3.1K',
    },
    {
      leftLabel: 'Net profit',
      rightLabel: 'Goal $62K',
      value: '$281K',
      footerUp: true,
      footerPct: '3%',
      footerAmt: '$9.4K',
    },
  ],
  '1M': [
    {
      leftLabel: 'Gross collection',
      rightLabel: 'Goal $380K',
      value: '$1.65M (64%)',
      footerUp: true,
      footerPct: '7%',
      footerAmt: '$112K',
    },
    {
      leftLabel: 'Net collection',
      rightLabel: 'Goal $350K',
      value: '$1.58M',
      footerUp: true,
      footerPct: '6%',
      footerAmt: '$98K',
    },
    {
      leftLabel: 'Adjustments',
      value: '$58K',
      footerUp: true,
      footerPct: '1%',
      footerAmt: '$12K',
    },
    {
      leftLabel: 'Net profit',
      rightLabel: 'Goal $240K',
      value: '$1.12M',
      footerUp: true,
      footerPct: '8%',
      footerAmt: '$86K',
    },
  ],
  '1Y': [
    {
      leftLabel: 'Gross collection',
      rightLabel: 'Goal $4.2M',
      value: '$18.2M (62%)',
      footerUp: true,
      footerPct: '5%',
      footerAmt: '$910K',
    },
    {
      leftLabel: 'Net collection',
      rightLabel: 'Goal $3.9M',
      value: '$17.4M',
      footerUp: true,
      footerPct: '4%',
      footerAmt: '$740K',
    },
    {
      leftLabel: 'Adjustments',
      value: '$620K',
      footerUp: true,
      footerPct: '2%',
      footerAmt: '$118K',
    },
    {
      leftLabel: 'Net profit',
      rightLabel: 'Goal $2.6M',
      value: '$12.4M',
      footerUp: true,
      footerPct: '6%',
      footerAmt: '$702K',
    },
  ],
  YTD: [
    {
      leftLabel: 'Gross collection',
      rightLabel: 'Goal $1.1M',
      value: '$4.85M (63%)',
      footerUp: true,
      footerPct: '5%',
      footerAmt: '$242K',
    },
    {
      leftLabel: 'Net collection',
      rightLabel: 'Goal $1.02M',
      value: '$4.62M',
      footerUp: true,
      footerPct: '4%',
      footerAmt: '$198K',
    },
    {
      leftLabel: 'Adjustments',
      value: '$168K',
      footerUp: true,
      footerPct: '2%',
      footerAmt: '$31K',
    },
    {
      leftLabel: 'Net profit',
      rightLabel: 'Goal $680K',
      value: '$3.28M',
      footerUp: true,
      footerPct: '6%',
      footerAmt: '$186K',
    },
  ],
}

export function getFinancePracticeRevenueCards(
  range: RangeKey = '1D',
): FinancePracticeRevenueCard[] {
  return PRACTICE_REVENUE_BY_RANGE[range]
}

type ProdCollChartRow = { label: string; Gross: number; Net: number }

type FinanceProdCollPanel = {
  prodGoalLabel: string
  collGoalLabel: string
  changeSignedPct: string
  changeAmt: string
  gross: string
  net: string
  adjustment: string
  rate: string
  prodChart: ProdCollChartRow[]
  prodYDomain: [number, number]
  prodYTicks: number[]
  prodGoalLine: number
  collChart: ProdCollChartRow[]
  collYDomain: [number, number]
  collYTicks: number[]
  collGoalLine: number
}

const PROD_COLL_BY_RANGE: Record<RangeKey, FinanceProdCollPanel> = {
  '1D': {
    prodGoalLabel: 'Goal 10K',
    collGoalLabel: 'Goal $350K',
    changeSignedPct: '+8%',
    changeAmt: '$630',
    gross: '$284.1K',
    net: '$254.6K',
    adjustment: '14.3%',
    rate: '250%',
    prodChart: [
      { label: '8 AM', Gross: 2.4, Net: 2.1 },
      { label: '11 AM', Gross: 6.2, Net: 5.6 },
      { label: '2 PM', Gross: 8.8, Net: 8.0 },
      { label: '5 PM', Gross: 9.4, Net: 8.6 },
    ],
    prodYDomain: [0, 12],
    prodYTicks: [0, 3, 6, 9, 12],
    prodGoalLine: 10,
    collChart: [
      { label: '8 AM', Gross: 210, Net: 190 },
      { label: '11 AM', Gross: 250, Net: 228 },
      { label: '2 PM', Gross: 276, Net: 252 },
      { label: '5 PM', Gross: 284.1, Net: 254.6 },
    ],
    collYDomain: [0, 400],
    collYTicks: [0, 100, 200, 300, 400],
    collGoalLine: 350,
  },
  '1W': {
    prodGoalLabel: 'Goal 58K',
    collGoalLabel: 'Goal $350K',
    changeSignedPct: '+5%',
    changeAmt: '$2.8K',
    gross: '$402K',
    net: '$361K',
    adjustment: '13.8%',
    rate: '242%',
    prodChart: [
      { label: 'Mon', Gross: 7.8, Net: 7.0 },
      { label: 'Tue', Gross: 8.4, Net: 7.6 },
      { label: 'Wed', Gross: 9.1, Net: 8.2 },
      { label: 'Thu', Gross: 8.9, Net: 8.0 },
      { label: 'Fri', Gross: 9.6, Net: 8.7 },
      { label: 'Sat', Gross: 4.2, Net: 3.8 },
      { label: 'Sun', Gross: 2.1, Net: 1.9 },
    ],
    prodYDomain: [0, 12],
    prodYTicks: [0, 3, 6, 9, 12],
    prodGoalLine: 10,
    collChart: [
      { label: 'Mon', Gross: 198, Net: 180 },
      { label: 'Tue', Gross: 220, Net: 200 },
      { label: 'Wed', Gross: 245, Net: 222 },
      { label: 'Thu', Gross: 238, Net: 216 },
      { label: 'Fri', Gross: 268, Net: 242 },
      { label: 'Sat', Gross: 120, Net: 108 },
      { label: 'Sun', Gross: 72, Net: 65 },
    ],
    collYDomain: [0, 400],
    collYTicks: [0, 100, 200, 300, 400],
    collGoalLine: 350,
  },
  '1M': {
    prodGoalLabel: 'Goal 240K',
    collGoalLabel: 'Goal $1.2M',
    changeSignedPct: '+6%',
    changeAmt: '$14K',
    gross: '$1.72M',
    net: '$1.55M',
    adjustment: '14.1%',
    rate: '248%',
    prodChart: [
      { label: 'W1', Gross: 38, Net: 34 },
      { label: 'W2', Gross: 42, Net: 38 },
      { label: 'W3', Gross: 45, Net: 40 },
      { label: 'W4', Gross: 48, Net: 43 },
    ],
    prodYDomain: [0, 60],
    prodYTicks: [0, 15, 30, 45, 60],
    prodGoalLine: 50,
    collChart: [
      { label: 'W1', Gross: 920, Net: 830 },
      { label: 'W2', Gross: 980, Net: 885 },
      { label: 'W3', Gross: 1040, Net: 938 },
      { label: 'W4', Gross: 1100, Net: 992 },
    ],
    collYDomain: [0, 1400],
    collYTicks: [0, 350, 700, 1050, 1400],
    collGoalLine: 1200,
  },
  '1Y': {
    prodGoalLabel: 'Goal 2.9M',
    collGoalLabel: 'Goal $5.2M',
    changeSignedPct: '+4%',
    changeAmt: '$118K',
    gross: '$18.6M',
    net: '$16.8M',
    adjustment: '13.6%',
    rate: '251%',
    prodChart: [
      { label: 'Q1', Gross: 4.2, Net: 3.8 },
      { label: 'Q2', Gross: 4.6, Net: 4.1 },
      { label: 'Q3', Gross: 4.9, Net: 4.4 },
      { label: 'Q4', Gross: 5.1, Net: 4.6 },
    ],
    prodYDomain: [0, 6],
    prodYTicks: [0, 2, 4, 6],
    prodGoalLine: 5,
    collChart: [
      { label: 'Q1', Gross: 4200, Net: 3780 },
      { label: 'Q2', Gross: 4580, Net: 4120 },
      { label: 'Q3', Gross: 4920, Net: 4430 },
      { label: 'Q4', Gross: 5180, Net: 4660 },
    ],
    collYDomain: [0, 6000],
    collYTicks: [0, 1500, 3000, 4500, 6000],
    collGoalLine: 5200,
  },
  YTD: {
    prodGoalLabel: 'Goal 820K',
    collGoalLabel: 'Goal $1.75M',
    changeSignedPct: '+5%',
    changeAmt: '$41K',
    gross: '$5.02M',
    net: '$4.52M',
    adjustment: '14.0%',
    rate: '249%',
    prodChart: [
      { label: 'Jan', Gross: 1.55, Net: 1.4 },
      { label: 'Feb', Gross: 1.62, Net: 1.46 },
      { label: 'Mar', Gross: 1.85, Net: 1.66 },
    ],
    prodYDomain: [0, 2.2],
    prodYTicks: [0, 0.6, 1.2, 1.8, 2.2],
    prodGoalLine: 2,
    collChart: [
      { label: 'Jan', Gross: 1480, Net: 1330 },
      { label: 'Feb', Gross: 1560, Net: 1405 },
      { label: 'Mar', Gross: 1680, Net: 1510 },
    ],
    collYDomain: [0, 2000],
    collYTicks: [0, 500, 1000, 1500, 2000],
    collGoalLine: 1750,
  },
}

export function getFinanceProductionCollPanel(
  range: RangeKey = '1D',
): FinanceProdCollPanel {
  return PROD_COLL_BY_RANGE[range]
}

type HalfSlice = { name: string; value: number; color: string }

/** Half-donut segment colors — Figma 5462-119244 */
export const FINANCE_INSURANCE_PATIENT_DONUT_COLORS: Record<string, string> = {
  Insurance: 'var(--blue-400)',
  Patient: 'var(--orange-300)',
}

export const FINANCE_EXPENSES_DONUT_COLORS: Record<string, string> = {
  Payroll: 'var(--blue-700)',
  Maintenance: 'var(--blue-500)',
  Supplies: 'var(--blue-400)',
  Marketing: 'var(--blue-200)',
  Lab: 'var(--orange-300)',
  Services: 'var(--orange-400)',
}

/** Legend order matches Figma 5462-119244 expenses card. */
export const FINANCE_EXPENSES_LEGEND_ORDER = [
  'Payroll',
  'Maintenance',
  'Supplies',
  'Marketing',
  'Lab',
  'Services',
] as const

export const FINANCE_DENTIST_HYGIENIST_DONUT_COLORS: Record<string, string> = {
  Dentist: 'var(--blue-400)',
  Hygienist: 'var(--orange-300)',
}

function withHalfDonutColors(
  slices: HalfSlice[],
  colorMap: Record<string, string>,
): HalfSlice[] {
  return slices.map((slice) => ({
    ...slice,
    color: colorMap[slice.name] ?? slice.color,
  }))
}

type FinanceSplitDonut = {
  metricLeft: string
  metricRight: string
  primaryK: number
  secondaryK: number
  slices: HalfSlice[]
}

const INSURANCE_PATIENT_BY_RANGE: Record<RangeKey, FinanceSplitDonut> = {
  '1D': {
    metricLeft: '$12.4K',
    metricRight: '$32.1K',
    primaryK: 12.4,
    secondaryK: 32.1,
    slices: [
      { name: 'Insurance', value: 12.4, color: 'var(--blue-500)' },
      { name: 'Patient', value: 32.1, color: 'var(--teal-400)' },
    ],
  },
  '1W': {
    metricLeft: '$58K',
    metricRight: '$154K',
    primaryK: 58,
    secondaryK: 154,
    slices: [
      { name: 'Insurance', value: 58, color: 'var(--blue-500)' },
      { name: 'Patient', value: 154, color: 'var(--teal-400)' },
    ],
  },
  '1M': {
    metricLeft: '$248K',
    metricRight: '$662K',
    primaryK: 248,
    secondaryK: 662,
    slices: [
      { name: 'Insurance', value: 248, color: 'var(--blue-500)' },
      { name: 'Patient', value: 662, color: 'var(--teal-400)' },
    ],
  },
  '1Y': {
    metricLeft: '$2.74M',
    metricRight: '$7.32M',
    primaryK: 2740,
    secondaryK: 7320,
    slices: [
      { name: 'Insurance', value: 2740, color: 'var(--blue-500)' },
      { name: 'Patient', value: 7320, color: 'var(--teal-400)' },
    ],
  },
  YTD: {
    metricLeft: '$728K',
    metricRight: '$1.94M',
    primaryK: 728,
    secondaryK: 1940,
    slices: [
      { name: 'Insurance', value: 728, color: 'var(--blue-500)' },
      { name: 'Patient', value: 1940, color: 'var(--teal-400)' },
    ],
  },
}

export function getFinanceInsurancePatientDonut(
  range: RangeKey = '1D',
): FinanceSplitDonut {
  const base = INSURANCE_PATIENT_BY_RANGE[range]
  return {
    ...base,
    slices: withHalfDonutColors(
      base.slices,
      FINANCE_INSURANCE_PATIENT_DONUT_COLORS,
    ),
  }
}

const EXPENSES_DONUT_BY_RANGE: Record<
  RangeKey,
  {
    totalOverhead: string
    expenseRatio: string
    primaryK: number
    secondaryK: number
    slices: HalfSlice[]
  }
> = {
  '1D': {
    totalOverhead: '$24.4K',
    expenseRatio: '29.1%',
    primaryK: 24.4,
    secondaryK: 29.1,
    slices: [
      { name: 'Payroll', value: 10, color: 'var(--blue-500)' },
      { name: 'Supplies', value: 5, color: 'var(--blue-400)' },
      { name: 'Maintenance', value: 3, color: 'var(--indigo-400)' },
      { name: 'Lab', value: 3, color: 'var(--yellow-400)' },
      { name: 'Services', value: 2, color: 'var(--teal-400)' },
      { name: 'Marketing', value: 1.4, color: 'var(--vivid-green-400)' },
    ],
  },
  '1W': {
    totalOverhead: '$118K',
    expenseRatio: '28.4%',
    primaryK: 118,
    secondaryK: 28.4,
    slices: [
      { name: 'Payroll', value: 48, color: 'var(--blue-500)' },
      { name: 'Supplies', value: 26, color: 'var(--blue-400)' },
      { name: 'Maintenance', value: 14, color: 'var(--indigo-400)' },
      { name: 'Lab', value: 12, color: 'var(--yellow-400)' },
      { name: 'Services', value: 10, color: 'var(--teal-400)' },
      { name: 'Marketing', value: 8, color: 'var(--vivid-green-400)' },
    ],
  },
  '1M': {
    totalOverhead: '$498K',
    expenseRatio: '29.8%',
    primaryK: 498,
    secondaryK: 29.8,
    slices: [
      { name: 'Payroll', value: 205, color: 'var(--blue-500)' },
      { name: 'Supplies', value: 108, color: 'var(--blue-400)' },
      { name: 'Maintenance', value: 62, color: 'var(--indigo-400)' },
      { name: 'Lab', value: 58, color: 'var(--yellow-400)' },
      { name: 'Services', value: 42, color: 'var(--teal-400)' },
      { name: 'Marketing', value: 23, color: 'var(--vivid-green-400)' },
    ],
  },
  '1Y': {
    totalOverhead: '$5.42M',
    expenseRatio: '30.2%',
    primaryK: 5420,
    secondaryK: 30.2,
    slices: [
      { name: 'Payroll', value: 2240, color: 'var(--blue-500)' },
      { name: 'Supplies', value: 1180, color: 'var(--blue-400)' },
      { name: 'Maintenance', value: 680, color: 'var(--indigo-400)' },
      { name: 'Lab', value: 620, color: 'var(--yellow-400)' },
      { name: 'Services', value: 460, color: 'var(--teal-400)' },
      { name: 'Marketing', value: 240, color: 'var(--vivid-green-400)' },
    ],
  },
  YTD: {
    totalOverhead: '$1.48M',
    expenseRatio: '29.5%',
    primaryK: 1480,
    secondaryK: 29.5,
    slices: [
      { name: 'Payroll', value: 612, color: 'var(--blue-500)' },
      { name: 'Supplies', value: 322, color: 'var(--blue-400)' },
      { name: 'Maintenance', value: 186, color: 'var(--indigo-400)' },
      { name: 'Lab', value: 170, color: 'var(--yellow-400)' },
      { name: 'Services', value: 126, color: 'var(--teal-400)' },
      { name: 'Marketing', value: 64, color: 'var(--vivid-green-400)' },
    ],
  },
}

export function getFinanceExpensesDonut(
  range: RangeKey = '1D',
): (typeof EXPENSES_DONUT_BY_RANGE)[RangeKey] {
  const base = EXPENSES_DONUT_BY_RANGE[range]
  return {
    ...base,
    slices: withHalfDonutColors(base.slices, FINANCE_EXPENSES_DONUT_COLORS),
  }
}

const DENTIST_HYGIENIST_BY_RANGE: Record<RangeKey, FinanceSplitDonut> = {
  '1D': {
    metricLeft: '$32.1K',
    metricRight: '$12.4K',
    primaryK: 32.1,
    secondaryK: 12.4,
    slices: [
      { name: 'Dentist', value: 32.1, color: 'var(--indigo-400)' },
      { name: 'Hygienist', value: 12.4, color: 'var(--indigo-300)' },
    ],
  },
  '1W': {
    metricLeft: '$158K',
    metricRight: '$61K',
    primaryK: 158,
    secondaryK: 61,
    slices: [
      { name: 'Dentist', value: 158, color: 'var(--indigo-400)' },
      { name: 'Hygienist', value: 61, color: 'var(--indigo-300)' },
    ],
  },
  '1M': {
    metricLeft: '$672K',
    metricRight: '$258K',
    primaryK: 672,
    secondaryK: 258,
    slices: [
      { name: 'Dentist', value: 672, color: 'var(--indigo-400)' },
      { name: 'Hygienist', value: 258, color: 'var(--indigo-300)' },
    ],
  },
  '1Y': {
    metricLeft: '$7.95M',
    metricRight: '$3.05M',
    primaryK: 7950,
    secondaryK: 3050,
    slices: [
      { name: 'Dentist', value: 7950, color: 'var(--indigo-400)' },
      { name: 'Hygienist', value: 3050, color: 'var(--indigo-300)' },
    ],
  },
  YTD: {
    metricLeft: '$2.12M',
    metricRight: '$812K',
    primaryK: 2120,
    secondaryK: 812,
    slices: [
      { name: 'Dentist', value: 2120, color: 'var(--indigo-400)' },
      { name: 'Hygienist', value: 812, color: 'var(--indigo-300)' },
    ],
  },
}

export function getFinanceDentistHygienistDonut(
  range: RangeKey = '1D',
): FinanceSplitDonut {
  const base = DENTIST_HYGIENIST_BY_RANGE[range]
  return {
    ...base,
    slices: withHalfDonutColors(
      base.slices,
      FINANCE_DENTIST_HYGIENIST_DONUT_COLORS,
    ),
  }
}

type TreatmentBar = { label: string; value: number }

const TREATMENT_VALUE_BY_RANGE: Record<
  RangeKey,
  {
    billed: string
    collected: string
    potential: string
    chart: TreatmentBar[]
    yDomain: [number, number]
    yTicks: number[]
  }
> = {
  '1D': {
    billed: '$24.1K',
    collected: '$9.4K',
    potential: '$16.4K',
    chart: TREATMENT_VALUE_DATA.map((r) => ({ ...r })),
    yDomain: [0, 30],
    yTicks: [0, 10, 20, 30],
  },
  '1W': {
    billed: '$112K',
    collected: '$44K',
    potential: '$76K',
    chart: [
      { label: 'Billed', value: 112 },
      { label: 'Collected', value: 44 },
      { label: 'Potential', value: 76 },
    ],
    yDomain: [0, 140],
    yTicks: [0, 35, 70, 105, 140],
  },
  '1M': {
    billed: '$482K',
    collected: '$188K',
    potential: '$326K',
    chart: [
      { label: 'Billed', value: 482 },
      { label: 'Collected', value: 188 },
      { label: 'Potential', value: 326 },
    ],
    yDomain: [0, 600],
    yTicks: [0, 150, 300, 450, 600],
  },
  '1Y': {
    billed: '$5.65M',
    collected: '$2.21M',
    potential: '$3.82M',
    chart: [
      { label: 'Billed', value: 120 },
      { label: 'Collected', value: 47 },
      { label: 'Potential', value: 81 },
    ],
    yDomain: [0, 140],
    yTicks: [0, 35, 70, 105, 140],
  },
  YTD: {
    billed: '$1.54M',
    collected: '$602K',
    potential: '$1.04M',
    chart: [
      { label: 'Billed', value: 154 },
      { label: 'Collected', value: 60 },
      { label: 'Potential', value: 104 },
    ],
    yDomain: [0, 180],
    yTicks: [0, 45, 90, 135, 180],
  },
}

export function getFinanceTreatmentValue(
  range: RangeKey = '1D',
): (typeof TREATMENT_VALUE_BY_RANGE)[RangeKey] {
  return TREATMENT_VALUE_BY_RANGE[range]
}

const AR_AGING_BY_RANGE: Record<
  RangeKey,
  {
    totalAR: string
    arRatio: string
    overdueAR: string
    arInsuranceMetric: string
    barData: { label: string; value: number }[]
    insuranceProgress: Record<ARBucket, number>
    patientProgress: Record<ARBucket, number>
  }
> = {
  '1D': {
    totalAR: '$24.1K',
    arRatio: '0.8',
    overdueAR: '$9.4K',
    arInsuranceMetric: '$9.4K',
    barData: AR_TOTAL_DATA.map((r) => ({ ...r })),
    insuranceProgress: { ...AR_INSURANCE_PROGRESS_DATA } as Record<
      ARBucket,
      number
    >,
    patientProgress: { ...AR_PATIENT_PROGRESS_DATA } as Record<
      ARBucket,
      number
    >,
  },
  '1W': {
    totalAR: '$112K',
    arRatio: '0.82',
    overdueAR: '$43K',
    arInsuranceMetric: '$43K',
    barData: [
      { label: '0-30', value: 46000 },
      { label: '31-60', value: 36000 },
      { label: '61-90', value: 18200 },
      { label: '90+', value: 11800 },
    ],
    insuranceProgress: {
      '0-30': 46000,
      '31-60': 36000,
      '61-90': 18200,
      '90+': 11800,
    },
    patientProgress: {
      '0-30': 19200,
      '31-60': 11000,
      '61-90': 8200,
      '90+': 4800,
    },
  },
  '1M': {
    totalAR: '$486K',
    arRatio: '0.85',
    overdueAR: '$186K',
    arInsuranceMetric: '$186K',
    barData: [
      { label: '0-30', value: 198000 },
      { label: '31-60', value: 156000 },
      { label: '61-90', value: 79200 },
      { label: '90+', value: 52800 },
    ],
    insuranceProgress: {
      '0-30': 198000,
      '31-60': 156000,
      '61-90': 79200,
      '90+': 52800,
    },
    patientProgress: {
      '0-30': 82800,
      '31-60': 47600,
      '61-90': 35400,
      '90+': 21200,
    },
  },
  '1Y': {
    totalAR: '$5.68M',
    arRatio: '0.88',
    overdueAR: '$2.18M',
    arInsuranceMetric: '$2.18M',
    barData: [
      { label: '0-30', value: 2310000 },
      { label: '31-60', value: 1820000 },
      { label: '61-90', value: 924000 },
      { label: '90+', value: 616000 },
    ],
    insuranceProgress: {
      '0-30': 2310000,
      '31-60': 1820000,
      '61-90': 924000,
      '90+': 616000,
    },
    patientProgress: {
      '0-30': 966000,
      '31-60': 554000,
      '61-90': 413000,
      '90+': 247000,
    },
  },
  YTD: {
    totalAR: '$1.55M',
    arRatio: '0.86',
    overdueAR: '$598K',
    arInsuranceMetric: '$598K',
    barData: [
      { label: '0-30', value: 630000 },
      { label: '31-60', value: 496000 },
      { label: '61-90', value: 252000 },
      { label: '90+', value: 168000 },
    ],
    insuranceProgress: {
      '0-30': 630000,
      '31-60': 496000,
      '61-90': 252000,
      '90+': 168000,
    },
    patientProgress: {
      '0-30': 263000,
      '31-60': 151000,
      '61-90': 112000,
      '90+': 67000,
    },
  },
}

export function getFinanceARAging(
  range: RangeKey = '1D',
): (typeof AR_AGING_BY_RANGE)[RangeKey] & {
  insuranceMax: number
  patientMax: number
} {
  const a = AR_AGING_BY_RANGE[range]
  const insuranceMax = Math.max(...Object.values(a.insuranceProgress))
  const patientMax = Math.max(...Object.values(a.patientProgress))
  return { ...a, insuranceMax, patientMax }
}

const CLAIMS_BY_RANGE: Record<
  RangeKey,
  {
    submitted: string
    paid: string
    pending: string
    denied: string
    chart: { label: string; value: number }[]
    yDomain: [number, number]
    yTicks: number[]
  }
> = {
  '1D': {
    submitted: '$42.1K',
    paid: '$26.4K',
    pending: '$14.6K',
    denied: '$4.4K',
    chart: CLAIMS_CHART_DATA.map((r) => ({ ...r })),
    yDomain: [0, 50],
    yTicks: [0, 10, 20, 30, 40, 50],
  },
  '1W': {
    submitted: '$198K',
    paid: '$124K',
    pending: '$68K',
    denied: '$21K',
    chart: [
      { label: 'Submitted', value: 198 },
      { label: 'Paid', value: 124 },
      { label: 'Pending', value: 68 },
      { label: 'Denied', value: 21 },
    ],
    yDomain: [0, 240],
    yTicks: [0, 60, 120, 180, 240],
  },
  '1M': {
    submitted: '$852K',
    paid: '$534K',
    pending: '$294K',
    denied: '$88K',
    chart: [
      { label: 'Submitted', value: 852 },
      { label: 'Paid', value: 534 },
      { label: 'Pending', value: 294 },
      { label: 'Denied', value: 88 },
    ],
    yDomain: [0, 1000],
    yTicks: [0, 250, 500, 750, 1000],
  },
  '1Y': {
    submitted: '$9.95M',
    paid: '$6.24M',
    pending: '$3.42M',
    denied: '$1.02M',
    chart: [
      { label: 'Submitted', value: 96 },
      { label: 'Paid', value: 60 },
      { label: 'Pending', value: 33 },
      { label: 'Denied', value: 10 },
    ],
    yDomain: [0, 120],
    yTicks: [0, 30, 60, 90, 120],
  },
  YTD: {
    submitted: '$2.71M',
    paid: '$1.70M',
    pending: '$932K',
    denied: '$278K',
    chart: [
      { label: 'Submitted', value: 271 },
      { label: 'Paid', value: 170 },
      { label: 'Pending', value: 93 },
      { label: 'Denied', value: 28 },
    ],
    yDomain: [0, 320],
    yTicks: [0, 80, 160, 240, 320],
  },
}

export function getFinanceClaims(
  range: RangeKey = '1D',
): (typeof CLAIMS_BY_RANGE)[RangeKey] {
  return CLAIMS_BY_RANGE[range]
}

const COLLECTION_LINE_BY_RANGE: Record<
  RangeKey,
  {
    goalLabel: string
    changeSignedPct: string
    changeAmt: string
    gross: string
    net: string
    goal: string
    rate: string
    chart: ProdCollChartRow[]
    yDomain: [number, number]
    yTicks: number[]
    refLine: number
  }
> = {
  '1D': {
    goalLabel: 'Goal $10K',
    changeSignedPct: '+8%',
    changeAmt: '$630',
    gross: '$8.4K',
    net: '$7.2K',
    goal: '$9K',
    rate: '14.3%',
    chart: COLLECTION_CHART_DATA.map((r) => ({ ...r })),
    yDomain: [0, 10],
    yTicks: [0, 4, 8, 10],
    refLine: 10,
  },
  '1W': {
    goalLabel: 'Goal $58K',
    changeSignedPct: '+5%',
    changeAmt: '$2.8K',
    gross: '$48.2K',
    net: '$41.6K',
    goal: '$52K',
    rate: '13.9%',
    chart: [
      { label: 'Mon', Gross: 6.8, Net: 5.9 },
      { label: 'Tue', Gross: 7.4, Net: 6.4 },
      { label: 'Wed', Gross: 8.1, Net: 7.0 },
      { label: 'Thu', Gross: 7.9, Net: 6.8 },
      { label: 'Fri', Gross: 8.6, Net: 7.5 },
      { label: 'Sat', Gross: 3.8, Net: 3.2 },
      { label: 'Sun', Gross: 1.9, Net: 1.6 },
    ],
    yDomain: [0, 12],
    yTicks: [0, 4, 8, 12],
    refLine: 10,
  },
  '1M': {
    goalLabel: 'Goal $240K',
    changeSignedPct: '+6%',
    changeAmt: '$14K',
    gross: '$206K',
    net: '$178K',
    goal: '$220K',
    rate: '14.0%',
    chart: [
      { label: 'W1', Gross: 48, Net: 42 },
      { label: 'W2', Gross: 52, Net: 45 },
      { label: 'W3', Gross: 56, Net: 49 },
      { label: 'W4', Gross: 50, Net: 44 },
    ],
    yDomain: [0, 70],
    yTicks: [0, 20, 40, 60, 70],
    refLine: 60,
  },
  '1Y': {
    goalLabel: 'Goal $2.9M',
    changeSignedPct: '+4%',
    changeAmt: '$118K',
    gross: '$2.48M',
    net: '$2.14M',
    goal: '$2.62M',
    rate: '13.7%',
    chart: [
      { label: 'Q1', Gross: 620, Net: 540 },
      { label: 'Q2', Gross: 680, Net: 590 },
      { label: 'Q3', Gross: 720, Net: 620 },
      { label: 'Q4', Gross: 460, Net: 390 },
    ],
    yDomain: [0, 800],
    yTicks: [0, 200, 400, 600, 800],
    refLine: 725,
  },
  YTD: {
    goalLabel: 'Goal $820K',
    changeSignedPct: '+5%',
    changeAmt: '$41K',
    gross: '$702K',
    net: '$608K',
    goal: '$748K',
    rate: '13.8%',
    chart: [
      { label: 'Jan', Gross: 218, Net: 190 },
      { label: 'Feb', Gross: 228, Net: 198 },
      { label: 'Mar', Gross: 256, Net: 220 },
    ],
    yDomain: [0, 300],
    yTicks: [0, 100, 200, 300],
    refLine: 260,
  },
}

export function getFinanceCollectionLineSection(
  range: RangeKey = '1D',
): (typeof COLLECTION_LINE_BY_RANGE)[RangeKey] & { changeLine: string } {
  const row = COLLECTION_LINE_BY_RANGE[range]
  return {
    ...row,
    changeLine: financeTrendLine(range, row.changeSignedPct, row.changeAmt),
  }
}

type FinanceFooterMetric = {
  value: string
  rightLabel?: string
  footerPct: string
  footerAmt: string
  footerUp: boolean
}

const COLLECTION_RATIO_BY_RANGE: Record<
  RangeKey,
  { patient: FinanceFooterMetric; insurance: FinanceFooterMetric }
> = {
  '1D': {
    patient: {
      value: '$6.3K',
      rightLabel: 'Goal 9K',
      footerPct: '+8%',
      footerAmt: '$630',
      footerUp: true,
    },
    insurance: {
      value: '$2.1K',
      rightLabel: 'Goal 9K',
      footerPct: '+8%',
      footerAmt: '$630',
      footerUp: true,
    },
  },
  '1W': {
    patient: {
      value: '$31K',
      rightLabel: 'Goal 42K',
      footerPct: '+5%',
      footerAmt: '$1.8K',
      footerUp: true,
    },
    insurance: {
      value: '$10.2K',
      rightLabel: 'Goal 14K',
      footerPct: '+4%',
      footerAmt: '$520',
      footerUp: true,
    },
  },
  '1M': {
    patient: {
      value: '$134K',
      rightLabel: 'Goal 180K',
      footerPct: '+6%',
      footerAmt: '$8.2K',
      footerUp: true,
    },
    insurance: {
      value: '$44K',
      rightLabel: 'Goal 60K',
      footerPct: '+5%',
      footerAmt: '$2.4K',
      footerUp: true,
    },
  },
  '1Y': {
    patient: {
      value: '$1.58M',
      rightLabel: 'Goal 2.1M',
      footerPct: '+4%',
      footerAmt: '$72K',
      footerUp: true,
    },
    insurance: {
      value: '$524K',
      rightLabel: 'Goal 700K',
      footerPct: '+3%',
      footerAmt: '$18K',
      footerUp: true,
    },
  },
  YTD: {
    patient: {
      value: '$428K',
      rightLabel: 'Goal 570K',
      footerPct: '+5%',
      footerAmt: '$22K',
      footerUp: true,
    },
    insurance: {
      value: '$142K',
      rightLabel: 'Goal 190K',
      footerPct: '+4%',
      footerAmt: '$6.8K',
      footerUp: true,
    },
  },
}

export function getFinanceCollectionRatioMetrics(
  range: RangeKey = '1D',
): (typeof COLLECTION_RATIO_BY_RANGE)[RangeKey] {
  return COLLECTION_RATIO_BY_RANGE[range]
}

/** Days-to-collect style targets (second Collection ratio card on Finance). */
const COLLECTION_RATIO_DSO_BY_RANGE: Record<
  RangeKey,
  {
    patientDays: string
    patientGoalLabel: string
    insuranceDays: string
    insuranceGoalLabel: string
    patientDaysNum: number
    insuranceDaysNum: number
    patientGoalDays: number
    insuranceGoalDays: number
  }
> = {
  '1D': {
    patientDays: '40 days',
    patientGoalLabel: 'Goal < 28 days',
    insuranceDays: '20 days',
    insuranceGoalLabel: 'Goal < 15 days',
    patientDaysNum: 40,
    insuranceDaysNum: 20,
    patientGoalDays: 28,
    insuranceGoalDays: 15,
  },
  '1W': {
    patientDays: '38 days',
    patientGoalLabel: 'Goal < 28 days',
    insuranceDays: '18 days',
    insuranceGoalLabel: 'Goal < 15 days',
    patientDaysNum: 38,
    insuranceDaysNum: 18,
    patientGoalDays: 28,
    insuranceGoalDays: 15,
  },
  '1M': {
    patientDays: '36 days',
    patientGoalLabel: 'Goal < 28 days',
    insuranceDays: '17 days',
    insuranceGoalLabel: 'Goal < 15 days',
    patientDaysNum: 36,
    insuranceDaysNum: 17,
    patientGoalDays: 28,
    insuranceGoalDays: 15,
  },
  '1Y': {
    patientDays: '34 days',
    patientGoalLabel: 'Goal < 28 days',
    insuranceDays: '16 days',
    insuranceGoalLabel: 'Goal < 15 days',
    patientDaysNum: 34,
    insuranceDaysNum: 16,
    patientGoalDays: 28,
    insuranceGoalDays: 15,
  },
  YTD: {
    patientDays: '35 days',
    patientGoalLabel: 'Goal < 28 days',
    insuranceDays: '17 days',
    insuranceGoalLabel: 'Goal < 15 days',
    patientDaysNum: 35,
    insuranceDaysNum: 17,
    patientGoalDays: 28,
    insuranceGoalDays: 15,
  },
}

export function getFinanceCollectionRatioDso(
  range: RangeKey = '1D',
): (typeof COLLECTION_RATIO_DSO_BY_RANGE)[RangeKey] {
  return COLLECTION_RATIO_DSO_BY_RANGE[range]
}

/** Grouped vertical bar chart: current DSO vs target (days) per payer type. */
export function getFinanceDsoGroupedBarChartPayload(range: RangeKey = '1D'): {
  data: { label: string; current: number; target: number }[]
  yDomain: [number, number]
  yTicks: number[]
} {
  const row = COLLECTION_RATIO_DSO_BY_RANGE[range]
  const data = [
    {
      label: 'Patient',
      current: row.patientDaysNum,
      target: row.patientGoalDays,
    },
    {
      label: 'Insurance',
      current: row.insuranceDaysNum,
      target: row.insuranceGoalDays,
    },
  ]
  const maxVal = Math.max(
    row.patientDaysNum,
    row.insuranceDaysNum,
    row.patientGoalDays,
    row.insuranceGoalDays,
  )
  const top = Math.max(20, Math.ceil((maxVal * 1.2) / 5) * 5)
  const yDomain: [number, number] = [0, top]
  const step = top / 5
  const yTicks = Array.from({ length: 6 }, (_, i) => Math.round(i * step))
  return { data, yDomain, yTicks }
}

const PRODUCTION_DEPT_BY_RANGE: Record<
  RangeKey,
  {
    dentists: FinanceFooterMetric
    hygienists: FinanceFooterMetric
    prodPerVisit: FinanceFooterMetric
    reworkRate: FinanceFooterMetric
  }
> = {
  '1D': {
    dentists: {
      value: '$6.3K',
      rightLabel: 'Goal 9K',
      footerPct: '+8%',
      footerAmt: '$630',
      footerUp: true,
    },
    hygienists: {
      value: '$2.1K',
      rightLabel: 'Goal 9K',
      footerPct: '+5%',
      footerAmt: '$185',
      footerUp: true,
    },
    prodPerVisit: {
      value: '$6.4K',
      rightLabel: 'Goal $220/visit',
      footerPct: '+6%',
      footerAmt: '$195',
      footerUp: true,
    },
    reworkRate: {
      value: '4.1%',
      rightLabel: 'Goal <5%',
      footerPct: '-0.3%',
      footerAmt: '0.3 pp',
      footerUp: false,
    },
  },
  '1W': {
    dentists: {
      value: '$31K',
      rightLabel: 'Goal 42K',
      footerPct: '+5%',
      footerAmt: '$1.8K',
      footerUp: true,
    },
    hygienists: {
      value: '$10.2K',
      rightLabel: 'Goal 14K',
      footerPct: '+4%',
      footerAmt: '$520',
      footerUp: true,
    },
    prodPerVisit: {
      value: '$31.2K',
      rightLabel: 'Goal $215/visit',
      footerPct: '+5%',
      footerAmt: '$1.9K',
      footerUp: true,
    },
    reworkRate: {
      value: '3.9%',
      rightLabel: 'Goal <5%',
      footerPct: '-0.2%',
      footerAmt: '0.2 pp',
      footerUp: false,
    },
  },
  '1M': {
    dentists: {
      value: '$134K',
      rightLabel: 'Goal 180K',
      footerPct: '+6%',
      footerAmt: '$8.2K',
      footerUp: true,
    },
    hygienists: {
      value: '$44K',
      rightLabel: 'Goal 60K',
      footerPct: '+5%',
      footerAmt: '$2.4K',
      footerUp: true,
    },
    prodPerVisit: {
      value: '$135K',
      rightLabel: 'Goal $218/visit',
      footerPct: '+6%',
      footerAmt: '$8.5K',
      footerUp: true,
    },
    reworkRate: {
      value: '3.6%',
      rightLabel: 'Goal <5%',
      footerPct: '-0.4%',
      footerAmt: '0.4 pp',
      footerUp: false,
    },
  },
  '1Y': {
    dentists: {
      value: '$1.58M',
      rightLabel: 'Goal 2.1M',
      footerPct: '+4%',
      footerAmt: '$72K',
      footerUp: true,
    },
    hygienists: {
      value: '$524K',
      rightLabel: 'Goal 700K',
      footerPct: '+3%',
      footerAmt: '$18K',
      footerUp: true,
    },
    prodPerVisit: {
      value: '$1.59M',
      rightLabel: 'Goal $225/visit',
      footerPct: '+4%',
      footerAmt: '$74K',
      footerUp: true,
    },
    reworkRate: {
      value: '3.2%',
      rightLabel: 'Goal <5%',
      footerPct: '-0.5%',
      footerAmt: '0.5 pp',
      footerUp: false,
    },
  },
  YTD: {
    dentists: {
      value: '$428K',
      rightLabel: 'Goal 570K',
      footerPct: '+5%',
      footerAmt: '$22K',
      footerUp: true,
    },
    hygienists: {
      value: '$142K',
      rightLabel: 'Goal 190K',
      footerPct: '+4%',
      footerAmt: '$6.8K',
      footerUp: true,
    },
    prodPerVisit: {
      value: '$430K',
      rightLabel: 'Goal $222/visit',
      footerPct: '+5%',
      footerAmt: '$23K',
      footerUp: true,
    },
    reworkRate: {
      value: '3.5%',
      rightLabel: 'Goal <5%',
      footerPct: '-0.2%',
      footerAmt: '0.2 pp',
      footerUp: false,
    },
  },
}

export function getFinanceProductionDeptMetrics(
  range: RangeKey = '1D',
): (typeof PRODUCTION_DEPT_BY_RANGE)[RangeKey] {
  return PRODUCTION_DEPT_BY_RANGE[range]
}

const SMALL_CARD_FOOTER_BY_RANGE: Record<
  RangeKey,
  {
    netHygiene: { value: string; pct: string; amt: string; up: boolean }
    perVisit: { value: string; pct: string; amt: string; up: boolean }
    reappointment: { value: string; pct: string; amt: string; up: boolean }
    ar90: { value: string; pct: string; amt: string; up: boolean }
  }
> = {
  '1D': {
    netHygiene: { value: '$8.4K', pct: '6%', amt: '$800', up: true },
    perVisit: { value: '$7.2K', pct: '8%', amt: '$630', up: true },
    reappointment: { value: '$2.5K', pct: '30%', amt: '$1,430', up: false },
    ar90: { value: '$3K', pct: '12%', amt: '$230', up: false },
  },
  '1W': {
    netHygiene: { value: '$41K', pct: '5%', amt: '$2.1K', up: true },
    perVisit: { value: '$35K', pct: '6%', amt: '$2.4K', up: true },
    reappointment: { value: '$11.8K', pct: '8%', amt: '$1.1K', up: false },
    ar90: { value: '$14.2K', pct: '4%', amt: '$680', up: false },
  },
  '1M': {
    netHygiene: { value: '$176K', pct: '7%', amt: '$12K', up: true },
    perVisit: { value: '$152K', pct: '6%', amt: '$9.8K', up: true },
    reappointment: { value: '$48K', pct: '5%', amt: '$2.8K', up: true },
    ar90: { value: '$58K', pct: '3%', amt: '$2.1K', up: true },
  },
  '1Y': {
    netHygiene: { value: '$2.08M', pct: '4%', amt: '$86K', up: true },
    perVisit: { value: '$1.79M', pct: '5%', amt: '$92K', up: true },
    reappointment: { value: '$562K', pct: '4%', amt: '$24K', up: true },
    ar90: { value: '$684K', pct: '2%', amt: '$16K', up: true },
  },
  YTD: {
    netHygiene: { value: '$568K', pct: '5%', amt: '$29K', up: true },
    perVisit: { value: '$488K', pct: '4%', amt: '$21K', up: true },
    reappointment: { value: '$154K', pct: '3%', amt: '$5.8K', up: true },
    ar90: { value: '$186K', pct: '2%', amt: '$4.2K', up: true },
  },
}

export function getFinanceSmallCardFooters(
  range: RangeKey = '1D',
): (typeof SMALL_CARD_FOOTER_BY_RANGE)[RangeKey] {
  return SMALL_CARD_FOOTER_BY_RANGE[range]
}

const BOTTOM_METRICS_BY_RANGE: Record<
  RangeKey,
  {
    collection: FinanceFooterMetric
    collectionRate: FinanceFooterMetric
    arAging: FinanceFooterMetric
    ar90: FinanceFooterMetric
  }
> = {
  '1D': {
    collection: {
      value: '$8.4K',
      rightLabel: 'Goal $9K',
      footerPct: '+8%',
      footerAmt: '$630',
      footerUp: true,
    },
    collectionRate: {
      value: '94.2%',
      rightLabel: 'Target 96%',
      footerPct: '+1.1%',
      footerAmt: '1.1 pp',
      footerUp: true,
    },
    arAging: {
      value: '$650',
      rightLabel: 'Goal $1K',
      footerPct: '-3%',
      footerAmt: '$22',
      footerUp: false,
    },
    ar90: {
      value: '$650',
      rightLabel: 'Goal $1K',
      footerPct: '-12%',
      footerAmt: '$230',
      footerUp: false,
    },
  },
  '1W': {
    collection: {
      value: '$41K',
      rightLabel: 'Goal $44K',
      footerPct: '+5%',
      footerAmt: '$2.1K',
      footerUp: true,
    },
    collectionRate: {
      value: '93.5%',
      rightLabel: 'Target 96%',
      footerPct: '+0.8%',
      footerAmt: '0.8 pp',
      footerUp: true,
    },
    arAging: {
      value: '$3.1K',
      rightLabel: 'Goal $4.5K',
      footerPct: '-4%',
      footerAmt: '$140',
      footerUp: false,
    },
    ar90: {
      value: '$3.1K',
      rightLabel: 'Goal $4.5K',
      footerPct: '-5%',
      footerAmt: '$180',
      footerUp: false,
    },
  },
  '1M': {
    collection: {
      value: '$176K',
      rightLabel: 'Goal $190K',
      footerPct: '+6%',
      footerAmt: '$11K',
      footerUp: true,
    },
    collectionRate: {
      value: '92.8%',
      rightLabel: 'Target 95%',
      footerPct: '+0.6%',
      footerAmt: '0.6 pp',
      footerUp: true,
    },
    arAging: {
      value: '$13.2K',
      rightLabel: 'Goal $19K',
      footerPct: '-5%',
      footerAmt: '$740',
      footerUp: false,
    },
    ar90: {
      value: '$13.2K',
      rightLabel: 'Goal $19K',
      footerPct: '-4%',
      footerAmt: '$620',
      footerUp: false,
    },
  },
  '1Y': {
    collection: {
      value: '$2.08M',
      rightLabel: 'Goal $2.24M',
      footerPct: '+4%',
      footerAmt: '$86K',
      footerUp: true,
    },
    collectionRate: {
      value: '91.2%',
      rightLabel: 'Target 94%',
      footerPct: '+0.4%',
      footerAmt: '0.4 pp',
      footerUp: true,
    },
    arAging: {
      value: '$156K',
      rightLabel: 'Goal $220K',
      footerPct: '-3%',
      footerAmt: '$5.2K',
      footerUp: false,
    },
    ar90: {
      value: '$156K',
      rightLabel: 'Goal $220K',
      footerPct: '-2%',
      footerAmt: '$3.8K',
      footerUp: false,
    },
  },
  YTD: {
    collection: {
      value: '$568K',
      rightLabel: 'Goal $612K',
      footerPct: '+5%',
      footerAmt: '$29K',
      footerUp: true,
    },
    collectionRate: {
      value: '92.1%',
      rightLabel: 'Target 95%',
      footerPct: '+0.5%',
      footerAmt: '0.5 pp',
      footerUp: true,
    },
    arAging: {
      value: '$42.5K',
      rightLabel: 'Goal $60K',
      footerPct: '-4%',
      footerAmt: '$1.9K',
      footerUp: false,
    },
    ar90: {
      value: '$42.5K',
      rightLabel: 'Goal $60K',
      footerPct: '-3%',
      footerAmt: '$1.4K',
      footerUp: false,
    },
  },
}

export function getFinanceBottomMetricCards(
  range: RangeKey = '1D',
): (typeof BOTTOM_METRICS_BY_RANGE)[RangeKey] {
  return BOTTOM_METRICS_BY_RANGE[range]
}

const PRODUCTION_BY_PROCEDURE_BY_RANGE: Record<
  RangeKey,
  {
    data: { label: string; value: number }[]
    yDomain: [number, number]
    yTicks: number[]
  }
> = {
  '1D': {
    data: PRODUCTION_BY_PROCEDURE_DATA.map((r) => ({ ...r })),
    yDomain: [0, 12],
    yTicks: [0, 3, 6, 9, 12],
  },
  '1W': {
    data: PRODUCTION_BY_PROCEDURE_DATA.map((r) => ({
      label: r.label,
      value: Math.round(r.value * 5.2 * 10) / 10,
    })),
    yDomain: [0, 52],
    yTicks: [0, 13, 26, 39, 52],
  },
  '1M': {
    data: PRODUCTION_BY_PROCEDURE_DATA.map((r) => ({
      label: r.label,
      value: Math.round(r.value * 20.8 * 10) / 10,
    })),
    yDomain: [0, 208],
    yTicks: [0, 52, 104, 156, 208],
  },
  '1Y': {
    data: PRODUCTION_BY_PROCEDURE_DATA.map((r) => ({
      label: r.label,
      value: Math.round(r.value * 12 * 10) / 10,
    })),
    yDomain: [0, 120],
    yTicks: [0, 30, 60, 90, 120],
  },
  YTD: {
    data: PRODUCTION_BY_PROCEDURE_DATA.map((r) => ({
      label: r.label,
      value: Math.round(r.value * 6 * 10) / 10,
    })),
    yDomain: [0, 60],
    yTicks: [0, 15, 30, 45, 60],
  },
}

export function getFinanceProductionByProcedure(
  range: RangeKey = '1D',
  maxCategories?: number,
): (typeof PRODUCTION_BY_PROCEDURE_BY_RANGE)[RangeKey] {
  const payload = PRODUCTION_BY_PROCEDURE_BY_RANGE[range]
  if (maxCategories == null) return payload

  const data = [...payload.data]
    .sort((a, b) => b.value - a.value)
    .slice(0, maxCategories)

  const { yDomain, yTicks } = computeProductionByProcedureCardAxis(
    data.map((row) => row.value),
  )

  return { data, yDomain, yTicks }
}

type ProductionByProcedureChartRow = {
  label: string
  value: number
  total: number
}

/** Mock chart + table rows for Production by procedure (Performance / Finance cards). */
export function buildFinanceProductionByProcedureDetailMock(
  range: RangeKey = '1D',
  maxCategories?: number,
): {
  rows: ProductionByProcedureRow[]
  chartConfig: {
    data: ProductionByProcedureChartRow[]
    yDomain: [number, number]
    yTicks: number[]
  }
  categoryColors: Record<string, string>
} {
  const mock = getFinanceProductionByProcedure(range, maxCategories)
  const chartData: ProductionByProcedureChartRow[] = mock.data.map((row) => ({
    label: row.label,
    value: row.value,
    total: row.value,
  }))
  const totalProduction =
    chartData.reduce((sum, row) => sum + row.value, 0) * 1000
  const rows: ProductionByProcedureRow[] = chartData.map((row) => {
    const production = row.value * 1000
    return {
      category: row.label,
      production,
      total_percentage:
        totalProduction > 0 ? (production / totalProduction) * 100 : 0,
      number_of_procedures: Math.max(1, Math.round(row.value * 2)),
    }
  })
  const categoryColors = Object.fromEntries(
    chartData.map((row) => [
      row.label,
      PRODUCTION_BY_PROCEDURE_COLORS[row.label] ?? 'var(--blue-400)',
    ]),
  )
  return {
    rows,
    chartConfig: {
      data: chartData,
      yDomain: mock.yDomain,
      yTicks: mock.yTicks,
    },
    categoryColors,
  }
}

/** Parse a mock currency cell like `$2,248.00` to a number for charts. */
export function parseFinanceCurrencyToNumber(value: string): number {
  const cleaned = value.replace(/[$,]/g, '')
  const n = parseFloat(cleaned)
  return Number.isFinite(n) ? n : 0
}

/** Short label for bar chart X-axis (e.g. `Dr. Jack Monroe` → `J. Monroe`). */
export function financeProviderChartLabel(providerName: string): string {
  const stripped = providerName.replace(/^Dr\.\s*/i, '').trim()
  const parts = stripped.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}. ${parts[parts.length - 1]}`
  }
  return providerName
}

function bumpMoneyCell(cell: string, factor: number): string {
  const m = cell.match(/^\$([\d,]+)\.(\d{2})$/)
  if (!m) return cell
  const n = Number(m[1].replace(/,/g, '')) * factor
  const formatted = n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `$${formatted}`
}

const PROVIDER_TABLE_FACTOR: Record<RangeKey, number> = {
  '1D': 1,
  '1W': 1.12,
  '1M': 1.38,
  '1Y': 6.5,
  YTD: 2.15,
}

export function getFinanceProductionPerProviderRows(
  range: RangeKey = '1D',
): Array<(typeof PRODUCTION_PER_PROVIDER_DATA)[number]> {
  const f = PROVIDER_TABLE_FACTOR[range]
  return PRODUCTION_PER_PROVIDER_DATA.map((row) => ({
    ...row,
    production: bumpMoneyCell(row.production, f),
  }))
}

const PROVIDER_TOTAL_BY_RANGE: Record<RangeKey, string> = {
  '1D': PRODUCTION_PER_PROVIDER_TOTAL,
  '1W': '$9,511.04',
  '1M': '$11,718.96',
  '1Y': '$55,198.00',
  YTD: '$18,257.80',
}

export function getFinanceProductionPerProviderTotal(
  range: RangeKey = '1D',
): string {
  return PROVIDER_TOTAL_BY_RANGE[range]
}
